import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ShopContext = createContext(null);

// LocalStorage key for guest cart (P1-6). Never mixed with the authenticated cart.
const GUEST_CART_KEY = "royal_lady_guest_cart_v2";

// Server-authoritative shipping mirror — matches _build_order_items_from_guest_input.
const FREE_SHIP_THRESHOLD = 999;
const SHIPPING_FEE = 99;

const _readGuestCart = () => {
  try {
    const raw = typeof window === "undefined" ? null : window.localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const _writeGuestCart = (items) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota errors */
  }
};

const _clearGuestCart = () => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(GUEST_CART_KEY);
  } catch {
    /* ignore */
  }
};

const _computeCartFromItems = (items) => {
  const priced = items.map((it) => {
    const p = it.product || {};
    const disc = Number(p.discount_percent || 0);
    const unit = Number(p.price || 0) * ((100 - disc) / 100);
    const line = Math.round(unit * (it.quantity || 1) * 100) / 100;
    return { ...it, unit_price: Math.round(unit * 100) / 100, line_total: line };
  });
  const subtotal = Math.round(priced.reduce((s, it) => s + it.line_total, 0) * 100) / 100;
  const shipping = subtotal >= FREE_SHIP_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_FEE;
  const total = Math.round((subtotal + shipping) * 100) / 100;
  return { items: priced, subtotal, shipping, total };
};

export const ShopProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, shipping: 0, total: 0 });
  const [wishlist, setWishlist] = useState([]);
  const guestMergedRef = useRef(false);

  const _hydrateGuestCart = useCallback(async () => {
    const stored = _readGuestCart();
    if (stored.length === 0) {
      setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
      return;
    }
    // Refresh product docs so images / stock / discounts are current.
    try {
      const ids = [...new Set(stored.map((it) => it.product_id))];
      const fresh = await Promise.all(
        ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
      );
      const byId = Object.fromEntries(fresh.filter(Boolean).map((p) => [p.id, p]));
      const items = stored
        .filter((it) => byId[it.product_id])
        .map((it) => ({ ...it, product: byId[it.product_id] }));
      // If any items were removed (deleted product), persist the trimmed list.
      if (items.length !== stored.length) {
        _writeGuestCart(items.map((it) => ({
          product_id: it.product_id, size: it.size, color: it.color, quantity: it.quantity,
        })));
      }
      setCart(_computeCartFromItems(items));
    } catch {
      setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
    }
  }, []);

  const loadCart = useCallback(async () => {
    if (!user) {
      await _hydrateGuestCart();
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {
      /* ignore */
    }
  }, [user, _hydrateGuestCart]);

  const loadWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data.products || []);
    } catch {
      /* ignore */
    }
  }, [user]);

  // Auto-merge guest cart into the server cart on first login of this session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user && !guestMergedRef.current) {
        const guestItems = _readGuestCart();
        if (guestItems.length > 0) {
          for (const it of guestItems) {
            try {
              await api.post("/cart", {
                product_id: it.product_id, size: it.size || "M", color: it.color || "",
                quantity: it.quantity || 1,
              });
            } catch {
              /* skip failures (OOS / stale product) — keeps sign-in smooth */
            }
          }
          _clearGuestCart();
          if (!cancelled) toast.success("Your saved bag has been merged");
        }
        guestMergedRef.current = true;
      }
      if (!user) guestMergedRef.current = false;
      loadCart();
      loadWishlist();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ------------- Cart mutations (user vs guest branching) -------------
  const addToCart = async (item) => {
    if (!user) {
      // Guest path — persist to localStorage.
      const stored = _readGuestCart();
      const idx = stored.findIndex(
        (x) => x.product_id === item.product_id && x.size === item.size && x.color === (item.color || "")
      );
      if (idx >= 0) {
        stored[idx].quantity += item.quantity || 1;
      } else {
        stored.push({
          product_id: item.product_id, size: item.size || "M",
          color: item.color || "", quantity: item.quantity || 1,
        });
      }
      _writeGuestCart(stored);
      await _hydrateGuestCart();
      toast.success("Added to bag");
      return { success: true, guest: true };
    }
    try {
      const { data } = await api.post("/cart", item);
      setCart(data);
      toast.success("Added to bag");
      return { success: true };
    } catch (err) {
      toast.error(formatApiError(err));
      return { success: false };
    }
  };

  const updateCartItem = async (index, quantity) => {
    if (!user) {
      const stored = _readGuestCart();
      if (index < 0 || index >= stored.length) return;
      if (quantity <= 0) {
        stored.splice(index, 1);
      } else {
        stored[index].quantity = quantity;
      }
      _writeGuestCart(stored);
      await _hydrateGuestCart();
      return;
    }
    const { data } = await api.patch(`/cart/${index}`, { quantity });
    setCart(data);
  };

  const removeCartItem = async (index) => {
    if (!user) {
      const stored = _readGuestCart();
      stored.splice(index, 1);
      _writeGuestCart(stored);
      await _hydrateGuestCart();
      return;
    }
    const { data } = await api.delete(`/cart/${index}`);
    setCart(data);
  };

  const clearCart = async () => {
    if (!user) {
      _clearGuestCart();
      setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
      return;
    }
    const { data } = await api.delete("/cart");
    setCart(data);
  };

  const isWishlisted = (pid) => wishlist.some((p) => p.id === pid);

  const toggleWishlist = async (product) => {
    if (!user) {
      toast.error("Please login to save to wishlist");
      return { needsAuth: true };
    }
    try {
      if (isWishlisted(product.id)) {
        const { data } = await api.delete(`/wishlist/${product.id}`);
        setWishlist(data.products || []);
        toast.message("Removed from wishlist");
      } else {
        const { data } = await api.post("/wishlist", { product_id: product.id });
        setWishlist(data.products || []);
        toast.success("Saved to wishlist");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cartCount = cart.items.reduce((n, it) => n + it.quantity, 0);

  return (
    <ShopContext.Provider
      value={{
        cart,
        wishlist,
        cartCount,
        addToCart,
        updateCartItem,
        removeCartItem,
        clearCart,
        toggleWishlist,
        isWishlisted,
        loadCart,
        loadWishlist,
        isGuest: !user,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => useContext(ShopContext);
